import * as Sentry from '@sentry/react'
import { useEffect, useRef } from 'react'
import { createBrowserRouter, RouterProvider, useRouteError } from 'react-router'

import ErrorView from '../views/ErrorView'
import PrivateRoute from './PrivateRoute'
import PublicRoute from './PublicRoute'
import routes from './routes'

// The error view is the only place where users actually notice an error, so we
// report the error here and ask for feedback instead of on every Sentry event.
const RouteErrorBoundary = () => {
  const error = useRouteError()
  // StrictMode runs effects twice in development; report each error only once.
  const reportedError = useRef<unknown>(null)

  useEffect(() => {
    if (reportedError.current === error) return
    reportedError.current = error

    const eventId = Sentry.captureException(error)
    Sentry.showReportDialog({
      title: 'Es sieht so aus als wäre ein Fehler aufgetreten.',
      subtitle: 'Das Entwicklerteam wurde benachrichtigt.',
      subtitle2: ' Können Sie uns kurz beschreiben, was zu dem Fehler geführt hat?',
      labelName: 'Name',
      labelEmail: 'Email',
      labelComments: 'Beschreibung Fehlerhergang',
      labelClose: 'Schließen',
      labelSubmit: 'Absenden',
      errorGeneric: 'Während des Hochladens Ihres Feedbacks ist ein Fehler aufgetreten.',
      errorFormEntry: 'Bitte vervollständigen Sie die Felder.',
      eventId,
      lang: 'de'
    })
  }, [error])

  return <ErrorView error={error instanceof Error ? error : new Error(String(error))} />
}

const router = createBrowserRouter(
  routes.map(({ path, component: Component, isPublic, restricted }) => ({
    path,
    element: isPublic ? (
      <PublicRoute component={Component} restricted={restricted} />
    ) : (
      <PrivateRoute>
        <Component />
      </PrivateRoute>
    ),
    errorElement: <RouteErrorBoundary />
  }))
)

const Router = () => {
  return <RouterProvider router={router} />
}

export default Router
